import DOMPurify from 'isomorphic-dompurify';

/**
 * Sanitizes HTML to prevent XSS attacks.
 * Uses isomorphic-dompurify which works in both SSR (via jsdom) and browser (native DOM).
 */
export function sanitizeHTML(html: string): string {
  return DOMPurify.sanitize(html);
}

/**
 * Sanitizes operator-authored HTML and drops the presentation it carries, keeping structure —
 * bold, italics, lists, links, line breaks.
 *
 * Pasting into the Back Office editor brings the source document's inline styling with it, so copy
 * taken from Word or an email arrives as `<span style="color: rgb(21, 66, 133)">` and renders in
 * that colour on our own banner, where it can clash or read as a link. An inline style also beats
 * any class we set, so this has to be removed rather than overridden. Legacy neutralises the same
 * thing in CSS, re-styling every pasted span (`VmTips` in
 * `vm-reservation-website/src/pages/garage-list/style.tsx`).
 */
export function sanitizeAuthoredHTML(html: string): string {
  return DOMPurify.sanitize(html, {
    FORBID_ATTR: ['style', 'class', 'bgcolor', 'color', 'face', 'size', 'align'],
  });
}

/**
 * True when `html` has something a customer would actually see.
 *
 * The Back Office rich-text editor saves markup rather than plain text, so a field the operator
 * cleared still arrives as `<p></p>` and is truthy. Ported from the legacy site
 * (`vm-reservation-website/src/common/sanitizer.ts`) so both booking sites agree on "empty".
 */
export function hasVisibleContent(html: string | undefined | null): boolean {
  if (!html) return false;
  const stripped = html.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim();
  return stripped.length > 0;
}
